import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Brain, Award, AlertTriangle, VolumeX, UserCheck,
  ChevronDown, ArrowRight, ExternalLink,
} from "lucide-react";
import { COMPARISON } from "../../data/content";
import { Reveal, SectionHeading } from "./Reveal";

const ICONS = { Brain, Award, AlertTriangle, VolumeX, UserCheck };

const CompareCard = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[item.icon];
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div
        className="h-full rounded-3xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7 flex flex-col hover:shadow-[0_24px_60px_-30px_rgba(42,49,146,0.35)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
        data-testid={`comparison-card-${index}`}
      >
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-navy to-brand-navy-deep dark:from-brand-sky dark:to-brand-sky flex items-center justify-center mb-5">
          <Icon size={19} className="text-white dark:text-brand-ink" />
        </div>
        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white mb-2.5">{item.title}</h3>
        <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 flex-1">{item.desc}</p>
        <button
          data-testid={`comparison-citation-toggle-${index}`}
          onClick={() => setOpen((o) => !o)}
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-brand-navy dark:text-brand-sky self-start"
          aria-expanded={open}
        >
          Learn More
          <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="overflow-hidden"
            >
              <div className="mt-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-4">
                <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">{item.citation.label}</p>
                <a
                  href={item.citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`comparison-citation-link-${index}`}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-brand-sky hover:underline"
                >
                  {item.citation.source} <ExternalLink size={10} />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

const Comparison = () => (
  <section id="why-human" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="comparison-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading
        id="comparison"
        overline="Human vs. AI-Only"
        title={<>Why choose human scribes <span className="text-brand-gradient">over AI-only scribing</span></>}
        sub="Ambient AI is advancing quickly — but published evaluations still favor human-authored documentation on the measures that matter most. Five evidence-led reasons practices choose human scribes."
      />
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {COMPARISON.map((item, i) => (
          <CompareCard key={item.title} item={item} index={i} />
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-14 flex justify-center">
          <Link
            to="/resources"
            data-testid="comparison-cta-button"
            className="group inline-flex items-center gap-2.5 rounded-full border-2 border-brand-navy dark:border-brand-sky text-brand-navy dark:text-brand-sky font-bold px-8 py-4 text-sm hover:bg-brand-navy hover:text-white dark:hover:bg-brand-sky dark:hover:text-brand-ink transition-colors duration-300"
          >
            Why More Physicians Still Choose Human Scribes
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
export default Comparison;
